/**
 * Converts an ISO timestamp (e.g., "2025-07-31T20:56:37.809Z") into a relative string:
 *   < 1 minute : "just now"
 *   < 1 hour   : "X minute(s) ago"
 *   < 1 day    : "X hour(s) ago"
 *   < 1 week   : "X day(s) ago"
 *   < 1 month  : "X week(s) ago"
 *   >= 1 month : formatted date like "Jan 1, 2024"
 *
 * NOTE: could be improved to be more user-friendly and calendar-oriented
 * (e.g., "yesterday", "last week", locale-aware phrasing, or handling future dates
 * as "in X"). This implementation sticks to the specified simple thresholds.
 */
export function timeAgo(isoTs: string): string {
  const now = new Date();
  const ts = new Date(isoTs);
  if (isNaN(ts.getTime())) {
    throw new Error(`Invalid ISO timestamp: ${isoTs}`);
  }

  const delta = now.getTime() - ts.getTime();
  if (delta < 0) {
    // future timestamp fallback
    return 'just now';
  }

  const second = 1000;
  const minute = 60 * second;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day; // approximate month

  if (delta < minute) {
    return 'just now';
  } else if (delta < hour) {
    const mins = Math.floor(delta / minute);
    return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
  } else if (delta < day) {
    const hrs = Math.floor(delta / hour);
    return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
  } else if (delta < week) {
    const days = Math.floor(delta / day);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  } else if (delta < month) {
    const weeks = Math.floor(delta / week);
    return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
  } else {
    // e.g., "Jan 1, 2024"
    return ts.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
}
